/**
 * Price History Service
 * 
 * Responsible for fetching price history data from the API.
 * Uses the price history processor for data analysis.
 */

import { fetchData } from './fetch.js';
import { analyzePriceHistory } from '../processors/price-history.processor.js';

/**
 * Fetch price history data for a game from the BGG marketplace API
 * 
 * @param {string} gameId - BGG game object ID
 * @returns {Promise<{sales: number[], averagePrice: number, outliers: number[], items: Array}>}
 */
export const fetchPriceHistory = async (gameId) => {
    try {
        const result = await fetchData(
            `/api/market/products/pricehistory?ajax=1&condition=any&currency=USD&objectid=${gameId}&objecttype=thing&pageid=1`
        );
        
        if (result && result.items && result.items.length > 0) {
            const analysis = analyzePriceHistory(result.items);
            
            return {
                sales: analysis.core,
                averagePrice: analysis.averagePrice,
                outliers: analysis.outliers,
                items: result.items
            };
        } else {
            console.info("No items found in price history for ID:", gameId);
            return { sales: [], averagePrice: 0, outliers: [], items: [] };
        }
    } catch (error) {
        console.error("Error fetching price history:", error);
        return { sales: [], averagePrice: 0, outliers: [], items: [] };
    }
};

/**
 * Fetch raw price history data without processing
 * Use this when you need the raw API response
 * 
 * @param {string} gameId - BGG game object ID
 * @returns {Promise<Object|null>} Raw API response or null on error
 */
export const fetchRawPriceHistory = async (gameId) => {
    try {
        const result = await fetchData(
            `/api/market/products/pricehistory?ajax=1&condition=any&currency=USD&objectid=${gameId}&objecttype=thing&pageid=1`
        );
        return result;
    } catch (error) {
        console.error("Error fetching raw price history:", error);
        return null;
    }
};

const priceHistoryService = {
    fetchPriceHistory,
    fetchRawPriceHistory
};

export default priceHistoryService;
